import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();
const TOKEN_STORAGE_KEY = 'motoringdaily_access_token';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function formatApiErrorDetail(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

function isTokenExpiredError(error) {
  const status = error?.response?.status;
  const detail = formatApiErrorDetail(error?.response?.data?.detail).toLowerCase();
  return status === 401 && detail.includes('token expired');
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://motoring-daily.onrender.com';

  const setAuthorizationHeader = useCallback((token) => {
    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common.Authorization;
    }
  }, []);

  useEffect(() => {
    console.info('[AuthContext] API base URL:', API_URL);
    if (!process.env.REACT_APP_BACKEND_URL) {
      console.warn('[AuthContext] REACT_APP_BACKEND_URL is undefined, using fallback Render URL.');
    }
    const savedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (savedToken) {
      setAuthorizationHeader(savedToken);
    }
  }, [API_URL, setAuthorizationHeader]);

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error?.config || {};
        const requestUrl = String(originalRequest?.url || '');
        const isAuthEndpoint = requestUrl.includes('/api/auth/login')
          || requestUrl.includes('/api/auth/register')
          || requestUrl.includes('/api/auth/logout')
          || requestUrl.includes('/api/auth/refresh');

        const hasSavedToken = Boolean(localStorage.getItem(TOKEN_STORAGE_KEY));
        const hasAuthHeader = Boolean(
          originalRequest?.headers?.Authorization || axios.defaults.headers.common.Authorization
        );

        if (originalRequest._retry || isAuthEndpoint || !(hasSavedToken || hasAuthHeader) || !isTokenExpiredError(error)) {
          throw error;
        }

        originalRequest._retry = true;

        try {
          const { data } = await axios.post(
            `${API_URL}/api/auth/refresh`,
            {},
            { withCredentials: true }
          );

          const refreshedToken = data?.access_token;
          if (refreshedToken) {
            localStorage.setItem(TOKEN_STORAGE_KEY, refreshedToken);
            setAuthorizationHeader(refreshedToken);
            originalRequest.headers = {
              ...(originalRequest.headers || {}),
              Authorization: `Bearer ${refreshedToken}`
            };
          }

          return axios({
            ...originalRequest,
            withCredentials: true
          });
        } catch (refreshError) {
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          setAuthorizationHeader(null);
          setUser(false);
          throw refreshError;
        }
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [API_URL, setAuthorizationHeader]);

  const checkAuth = useCallback(async () => {
    try {
      console.info('[AuthContext] Checking auth at:', `${API_URL}/api/auth/me`);
      const { data } = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      console.info('[AuthContext] checkAuth response user:', data?.email || null);
      setUser(data);
    } catch (error) {
      console.error('[AuthContext] checkAuth failed:', error?.response?.data || error.message || error);
      if (error?.response?.status === 401) {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        setAuthorizationHeader(null);
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [API_URL, setAuthorizationHeader]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const register = useCallback(async (email, password, name) => {
    try {
      console.info('[AuthContext] Register request:', `${API_URL}/api/auth/register`);
      const { data } = await axios.post(
        `${API_URL}/api/auth/register`,
        { email, password, name },
        { withCredentials: true }
      );
      if (data?.access_token) {
        localStorage.setItem(TOKEN_STORAGE_KEY, data.access_token);
        setAuthorizationHeader(data.access_token);
      }
      setUser(data);
      return { success: true };
    } catch (error) {
      const detailed = formatApiErrorDetail(error.response?.data?.detail);
      return {
        success: false,
        error: detailed || error.message || 'Something went wrong. Please try again.'
      };
    }
  }, [API_URL, setAuthorizationHeader]);

  const login = useCallback(async (email, password) => {
    try {
      console.info('[AuthContext] Login request:', `${API_URL}/api/auth/login`);
      const { data } = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      console.info('[AuthContext] Login response user:', data?.email || null);
      if (data?.access_token) {
        localStorage.setItem(TOKEN_STORAGE_KEY, data.access_token);
        setAuthorizationHeader(data.access_token);
      }
      setUser(data);
      return { success: true };
    } catch (error) {
      console.error('[AuthContext] Login failed:', error?.response?.data || error.message || error);
      const detailed = formatApiErrorDetail(error.response?.data?.detail);
      return {
        success: false,
        error: detailed || error.message || 'Something went wrong. Please try again.'
      };
    }
  }, [API_URL, setAuthorizationHeader]);

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      setAuthorizationHeader(null);
      setUser(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const value = {
    user,
    loading,
    register,
    login,
    logout,
    checkAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};